import { ENTERPRISE_COMPANY_TYPE } from '@consolidocs-ui/utils';
import { CompanyType, OrganizationPayload } from '@consolidocs-ui/types';

type Errors = Partial<Record<keyof CompanyType, string>>;

const text = (value?: string): string => value?.trim() ?? '';

/**
 * Pulls the new organization's id out of the create response, tolerating both
 * a bare object and the `{ data: … }` envelope other endpoints use.
 */
export const extractOrganizationId = (
  response: unknown
): string | undefined => {
  const body = response as
    | { id?: string; data?: { id?: string } }
    | undefined
    | null;
  return body?.id || body?.data?.id;
};

/**
 * Maps the wizard state onto the organization API contract.
 *
 * - Tenant identifiers (`subdomain`, `slug`) and the provisioning flags are
 *   enterprise-only, so they are dropped entirely for a general organization.
 * - The provisioning flags are backend-managed: they are seeded as `false` on
 *   create and never sent on update, so a PATCH can't reset real infra state.
 */
export const buildOrganizationPayload = (
  formData: CompanyType,
  { isEdit = false }: { isEdit?: boolean } = {}
): OrganizationPayload => {
  const payload: OrganizationPayload = {
    industry: text(formData.industry),
    contract_types: formData.contract_types ?? [],
    name: text(formData.name),
    description: text(formData.description),
    email: text(formData.email),
    phone: text(formData.phone),
    website: text(formData.website),
    address1: text(formData.address1),
    address2: text(formData.address2),
    city: text(formData.city),
    state: text(formData.state),
    postalCode: text(formData.postalCode),
    country: text(formData.country),
    companyType: text(formData.companyType),
    is_active: formData.is_active ?? true,
    is_deleted: formData.is_deleted ?? false,
  };

  if (formData.companyType !== ENTERPRISE_COMPANY_TYPE) {
    return payload;
  }

  const subdomain = text(formData.subdomain);
  const slug = text(formData.slug);
  if (subdomain) payload.subdomain = subdomain;
  if (slug) payload.slug = slug;

  if (!isEdit) {
    payload.isDbCreated = false;
    payload.isCognitoPoolCreated = false;
    payload.isSqsReady = false;
    payload.isServiceReady = false;
  }

  return payload;
};

/**
 * Step 1 — organization / company details validation.
 * Kept identical to the previous single-step behaviour so nothing regresses
 * for the general-organization path.
 */
export const validateStep1 = (
  formData: CompanyType,
  setErrors: React.Dispatch<React.SetStateAction<Errors>>
): boolean => {
  const newErrors: Errors = {};

  if (!formData.name.trim()) newErrors.name = 'Company name is required';
  if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email))
    newErrors.email = 'Valid email is required';
  if (formData.phone && !/^\+?[0-9\s-]{7,15}$/.test(formData.phone))
    newErrors.phone = 'Invalid phone number';
  if (formData.website && !/^https?:\/\/.+\..+/.test(formData.website))
    newErrors.website = 'Invalid website URL';
  if (!formData.address1.trim())
    newErrors.address1 = 'Address Line 1 is required';
  if (!formData.city.trim()) newErrors.city = 'City is required';
  if (!formData.state.trim()) newErrors.state = 'State/Province is required';
  if (!formData.postalCode.trim())
    newErrors.postalCode = 'Postal code is required';
  if (!formData.country.trim()) newErrors.country = 'Country is required';
  if (!formData?.companyType?.trim())
    newErrors.companyType = 'Company Type is required';
  if (!formData?.description?.trim())
    newErrors.description = 'Description is required';
  if (!formData.industry?.trim()) newErrors.industry = 'Industry is required';
  if (!formData.contract_types?.length)
    newErrors.contract_types = 'At least one contract type is required';

  setErrors((prev) => ({ ...prev, ...newErrors }));
  return Object.keys(newErrors).length === 0;
};

/**
 * Step 2 — infrastructure configuration validation (only for Enterprise).
 *
 * Contract: both fields are optional (`required: false, unique`). The
 * provisioning status flags (`isDbCreated`, `isCognitoPoolCreated`,
 * `isSqsReady`, `isServiceReady`) are read-only, backend-managed, and never
 * validated on the FE. Uniqueness is enforced by the backend.
 */
export const validateStep2 = (
  formData: CompanyType,
  setErrors: React.Dispatch<React.SetStateAction<Errors>>
): boolean => {
  const newErrors: Errors = {};

  // Subdomain — lowercase letters, digits, hyphens (no leading/trailing hyphen).
  const subdomainRegex = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
  if (formData.subdomain?.trim() && !subdomainRegex.test(formData.subdomain.trim())) {
    newErrors.subdomain =
      'Only lowercase letters, digits and hyphens are allowed (no leading/trailing hyphen).';
  }

  // Org slug — lowercase letters, digits, hyphens, underscores.
  const slugRegex = /^[a-z0-9]([a-z0-9_-]{0,61}[a-z0-9])?$/;
  if (formData.slug?.trim() && !slugRegex.test(formData.slug.trim())) {
    newErrors.slug =
      'Only lowercase letters, digits, hyphens and underscores are allowed.';
  }

  setErrors((prev) => ({ ...prev, ...newErrors }));
  return Object.keys(newErrors).length === 0;
};

/**
 * Kept for backwards compatibility with any external callers — always
 * validates Step 1 fields.
 */
export const validate = validateStep1;

